import { DraftOrder } from "@/common/services/purchaseRequisition.api";
import { useUser } from "@/common/services/users.api";
import { useGetAllPurchaseRequestors } from "@/modules/invoice/hook";
import { IPurchaseRequisition, STATUS_ITEM } from "@/types/purchaseRequisition.type";
import { useState } from "react";
import { HiOutlineDownload } from "react-icons/hi";
import { HiOutlinePaperClip } from "react-icons/hi2";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { getCode } from "@/lib/utils";
import { IPurchaseOrderItem, PURCHASEORDER_STATUS, emptyPurchaseOrder } from "@/types/purchaseOrder.types";
import { createPurchaseOrder } from "@/common/services/purchaseOrder.api";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { useCompanyStore } from "@/common/states/store/Company";
import { Button, Modal, Textarea } from "flowbite-react";
import { useRejectedPurchaseOrdem } from "@/common/hooks";

type Props = {
  requisition: Partial<IPurchaseRequisition> & { budgets: DraftOrder[] }
  closeModal?: () => void
}

const PRIORITY_LABELS: Record<string, string> = {
  HIGH: "Alta",
  MEDIUM: "Média",
  LOW: "Baixa",
}

export default function ModalAuthorizedPurchaseRequisition({ requisition, closeModal }: Props) {
  const [loading, setLoading] = useState(false);
  const [isOpenModalRejectOrder , setIsOpenModalRejectOrder] = useState(false);
  const items = requisition.budgets ?? []
  const licensePlate = requisition.licensePlate ?? ""
  const observation = requisition.observation ?? ""
  const createdAt = requisition.date ? new Date(requisition.date) : null
  const priority =
    requisition.priority && PRIORITY_LABELS[requisition.priority] ? PRIORITY_LABELS[requisition.priority] : "Média"
  const code = requisition?.code ?? ""
  const requester = requisition?.requestorName ?? ""

  const user = useUser();
  const { notifyError, notifySuccess } = useToastContext();
  const { data: requestors = [] } = useGetAllPurchaseRequestors();
  const companyId = useCompanyStore((state) => state.company?.companyId);

  const CATEGORY_ORDER = {
   id: "49220f39-89d5-4daa-a95a-d20b2d3a3c08",
   name: "Compra de Peças	"
  }

  const COST_CENTER_OPERATIONAL = {
   id: "70d90ad9-9ca2-4186-a016-e36b2efa7eb3",
   name: "OPERAÇÃO",
  };

  const formatDate = (d?: Date | null | string) =>
    d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" }) : "-"

  const formatCurrency = (value?: number) => {
    if (!value && value !== 0) return "-"
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value)
  }

  const onSave = async () => {
    setLoading(true);
    try {
      const requestor = requestors.find(
        (req) => req.employeeEmail === user.email,
      );

      if (!requestor) {
        notifyError("Usuário solicitante não encontrado.");
        setLoading(false);
        return;
      }

      for (const budget of requisition.budgets) {
        const code = getCode();
        const defaultValues = emptyPurchaseOrder();
        const now = new Date();

        const items: IPurchaseOrderItem[] = budget.items.map(item => {
          return {
              description: String(item.productName), 
              qtd: Number(item.approvedQuantity),
              value: item.quoteValue || 0,
              productId: item.productId,
          };
        });

        const totalValue = items.reduce((acc, item) => {
          const value = Number(item.value) || 0;
          const qtd = Number(item.qtd) || 0;
          return acc + value * qtd;
        }, 0);

        const purchaseOrder = {
          ...defaultValues,
          description: `Ordem de compra gerada a partir da requisição ${requisition.code}`,
          companyId: companyId || "",
          requestorId: requestor._id,
          requestorName: requestor.name,
          requestorEmail: requestor.employeeEmail,
          code: code.toString(),
          status: PURCHASEORDER_STATUS.PRE_APPROVED,
          supplierId: budget.supplierId,
          supplierName: budget.supplierName,
          financial: {
            ...defaultValues.financial,
            categoryId: CATEGORY_ORDER.id,
            categoryName: CATEGORY_ORDER.name,
            costCenterId: COST_CENTER_OPERATIONAL.id,
            costCenterName: COST_CENTER_OPERATIONAL.name,
            value: totalValue,
            expectedDate: now,
            instalment: [
              {
                installmentNumber: "1",
                value: totalValue,
                dueDate: now,
              },
            ],
          },
          itens: items,
          createdAt: now,
        };

        const result = await createPurchaseOrder(purchaseOrder);
        if (result.ok) {
          notifySuccess(`Ordem de compra para ${budget.supplierName} gerada com sucesso.`);
        } else {
          notifyError(
            result.message || `Erro ao gerar ordem de compra para ${budget.supplierName}`,
          );
        }
      }
      closeModal?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-4">
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white z-10 w-full max-w-5xl rounded-2xl shadow-2xl p-8 relative overflow-y-auto max-h-[90vh] border border-gray-200"
      >
        <button
          onClick={closeModal}
          aria-label="Close modal"
          className="absolute top-3 right-4 text-gray-500 hover:text-red-500 text-2xl font-bold transition"
        >
          ×
        </button>

        <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-3">Requisição de Compra</h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="md:col-span-1">
            <p className="text-xs text-gray-500">Código</p>
            <p className="font-semibold text-gray-800">{String(code || "-")}</p>
          </div>

          <div className="md:col-span-1">
            <p className="text-xs text-gray-500">Prioridade</p>
            <p className="font-semibold text-gray-800">{priority}</p>
          </div>
          <div className="md:col-span-1">
            <p className="text-xs text-gray-500">Placa</p>
            <p className="font-semibold text-gray-800">{licensePlate || "-"}</p>
          </div>
          <div className="md:col-span-1">
            <p className="text-xs text-gray-500">Data</p>
            <p className="font-semibold text-gray-800">{formatDate(createdAt)}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-xs text-gray-500">Solicitante</p>
            <p className="font-semibold text-gray-800">{requester || "-"}</p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Orçamentos</h3>
        {items.length === 0 ? (
          <div className="border border-gray-200 rounded-lg p-4 text-sm text-gray-600 mb-6">
            Nenhum orçamento informado.
          </div>
        ) : (
          <div className="mb-6 space-y-4">
            {items.map((budget, idx) => (
              <div key={budget?.supplierId || idx} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-800 text-base">
                        {budget.supplierName || "Fornecedor não informado"}
                      </p>
                      {(budget?.files || [])?.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between">
                            <h2 className="mb-2 font-medium">DOCUMENTOS ANEXADOS</h2>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {(budget?.files as string[])?.map((file: string) => (
                              <a
                                key={file}
                                href={`https://assets.locavibe.com.br/documents/nibo/${file}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="col-span-1 flex items-center gap-3 bg-white border border-gray-200 p-3 rounded shadow-sm hover:bg-gray-50 transition"
                              >
                                <span className="p-2 rounded-full flex items-center justify-center">
                                  <HiOutlinePaperClip className="h-5 w-5 text-black" />
                                </span>
                                <span className="flex-1 truncate text-blue-700 underline text-sm">
                                  {file}
                                </span>
                                <HiOutlineDownload className="h-4 w-4 text-gray-600" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-4 mt-1 text-xs text-gray-600">
                        {budget.deliveryDate && (
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            Entrega: {formatDate(budget.deliveryDate || "")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Valor Total</p>
                      <p className="text-lg font-bold text-green-600">
                        {formatCurrency(
                          budget.items.reduce((acc, current) => acc + ((Number(current.quoteValue) * Number(current.approvedQuantity)) || 0), 0)
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {budget.items && budget.items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">#</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Qtd</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Valor Unit.</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {budget.items.map((item, itemIdx: number) => (
                          <tr key={itemIdx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-500">{itemIdx + 1}</td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-800">{item.productName}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{item.approvedQuantity}</p>
                            </td>
                            <td className="px-4 py-3 text-center text-gray-700">{item.approvedQuantity || "-"}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(Number(item.quoteValue || 0))}</td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-800">
                              {formatCurrency((Number(item.approvedQuantity) || 0) * (Number(item.quoteValue) || 0))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                    Nenhum item informado neste orçamento
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mb-8">
          <p className="text-sm font-semibold mb-1 text-gray-700">Observações</p>
          <div className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 bg-gray-50 min-h-[72px]">
            {observation?.trim() ? observation : "—"}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => setIsOpenModalRejectOrder(true)}
            className="mt-2 w-32 font-semibold transition py-2 px-4 rounded-md border bg-gray5 text-gray-700 hover:bg-gray-100"
          >
            Rejeitar
            </button>
          &nbsp;&nbsp;
          <ButtonWithLoader
            isLoading={loading}
            onClick={onSave}
            className="mt-2 w-56 font-semibold transition py-2 px-4 rounded-md bg-locavibe text-white hover:bg-red-700"
          >
            Autorizar
          </ButtonWithLoader>
        </div>
        {isOpenModalRejectOrder && (
          <ModalJustificationRejectedPurchaseRequisition 
            closeRejectedModal={() => setIsOpenModalRejectOrder(false)} 
            purchaseRequisitionId={requisition._id} 
          />
        )}
      </div>
    </div>
  )
}

export const ModalJustificationRejectedPurchaseRequisition = ({closeRejectedModal, purchaseRequisitionId}) => {
  const [reason, setReason] = useState("");
  const rejectedPurchaseOrdem = useRejectedPurchaseOrdem();
  const { notifySuccess, notifyError } = useToastContext();

  const handleCancel = async () => {
    try {
      await rejectedPurchaseOrdem.mutateAsync(
        { 
          requisitionId: purchaseRequisitionId, 
          status: STATUS_ITEM.REJECTED, 
          reason 
        });

      closeRejectedModal();
      notifySuccess("Processando pedido de cancelamento");
    } catch (err) {
      notifyError((err as Error)?.message || "Erro ao reprovar requisição de compra");
    }
  };

  return (
    <Modal show={true} onClose={closeRejectedModal}>
      <Modal.Header>Reprovar ordem de compra</Modal.Header>
      <Modal.Body>
        <p className="mb-3">Informe o motivo da reprovação:</p>
        <Textarea
          placeholder="Motivo da reprovação"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={closeRejectedModal}>
          Fechar
        </Button>
        <ButtonWithLoader
          onClick={handleCancel}
          disabled={rejectedPurchaseOrdem.isPending}
          isLoading={rejectedPurchaseOrdem.isPending}
        >
          {rejectedPurchaseOrdem.isPending ? "Processando..." : "Confirmar"}
        </ButtonWithLoader>
      </Modal.Footer>
    </Modal>
  );
}

